import React from 'react';
import { Tooltip } from 'antd';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { usePatientVirtualOnly } from '@hooks/usePatientVirtualOnly';

const REASON_MAP: Record<string, string> = {
  AWAITING_INFO:
    'Your case is limited to virtual-only services while we await further information regarding your policy details. In-person clinical services will not be available.',
  DECISION_MADE:
    'Your case is limited to virtual-only services based on your policy details. In-person clinical services will not be available.'
};

const LabelWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px !important;

  font-size: 1.125rem;
  font-weight: 600;
  color: var(--productPrimaryDark);
  padding-bottom: 0.5rem;
  padding-left: 0.5rem;
  cursor: default;

  & > .anticon,
  & > svg,
  & > span {
    margin: 0 !important;
    padding: 0 !important;
    flex: none !important;
  }

  span {
    font-size: 0.8125rem;
    text-transform: capitalize;
  }
`;

const TooltipContent = ({ reason }: { reason: string }) => {
  const { t } = useTranslation('common');
  const mappedReason = REASON_MAP?.[reason];

  return (
    <div>
      <p>{t(mappedReason ?? 'Case marked as Virtual Only. In-person clinical services will not be available.')}</p>
    </div>
  );
};

interface VirtualOnlyStatusProps {
  caseId: number;
  className?: string;
}

const VirtualOnlyLabel = ({ caseId, className }: VirtualOnlyStatusProps) => {
  const { t } = useTranslation('common');
  const { isVirtualOnly, reason } = usePatientVirtualOnly(caseId);

  if (!isVirtualOnly) return null;

  return (
    <Tooltip placement="topLeft" title={<TooltipContent reason={reason} />} trigger={['hover', 'focus']}>
      <LabelWrapper className={className} data-testid="virtual-only-status">
        <AiOutlineInfoCircle aria-hidden style={{ verticalAlign: 'middle', fontSize: 20 }} />
        <span>{t('Virtual Only')}</span>
      </LabelWrapper>
    </Tooltip>
  );
};

export default VirtualOnlyLabel;
